import React from 'react';
import {
    RawPurchaseEntryBase,
    areRawPurchaseEntryFieldOptionsEqual,
    buildUpdatedRawPurchaseEntryFields,
    buildRawPurchaseEntryDetailPanelTypes,
    buildRawPurchaseEntryHiddenPanelTypes,
    buildRawPurchaseEntryTotalPanelDefinitions,
    RAW_PURCHASE_ENTRY_FIELD_IDS
} from './RawPurchaseEntryBase';
import { DeepCopy, DicVal, GetDesitRound, GetRound, NumConv, isEmpty } from '../modules/appLibrary';

const HEADER_FIELD_IDS = {
    referenceSlipNo: RAW_PURCHASE_ENTRY_FIELD_IDS.referenceSlipNo,
    denKbn: "ZSR_DENKBN",
    denSyubetu: "ZSR_DENSYUBETU",
    kaigaiKbn: "ZSR_KAIGAIKBN",
    shanaiRate: "ZSR_SHANAIRATE",
    invoiceNo: "ZSR_INVOICENO",
    nebikiNemasi: "ZSR_NEBIKINEMASI",
    sanMotoDenKbn: RAW_PURCHASE_ENTRY_FIELD_IDS.sourceCategoryCode
};

const DETAIL_COLUMN_IDS = {
    zaiHinban: "ZSRM_ZAIHINBAN",
    zaiColor: "ZSRM_ZAICOLORCD",
    tanka: "ZSRM_TANKA",
    tankaF: "ZSRM_TANKAF",
    meterTanka: "ZSRM_METERTANKA",
    meterTankaF: "ZSRM_METERTANKAF",
    siireKingaku: "ZSRM_SIIREKINGAKU",
    siireKingakuF: "ZSRM_SIIREKINGAKUF",
    kingakuZ: "ZSRM_KINGAKUZ",
    siireTanSu: "ZSRM_SIIRETANSU",
    siireMeterSu: "ZSRM_SIIREMETERSU",
    siireSu: "ZSRM_SIIRESU",
    ssdNo: "ZSRM_SSDNO"
};

const DETAIL_PANEL_TYPES = {
    zai: "710",
    fzk: "720",
    sub: "730"
};

const SOURCE_CATEGORY_CODES = {
    rawSalesIn: "5",
    rawIntTrade: "6",
    rawPurchase: "7"
};

const detailPanelTypesByMaterialCategory = buildRawPurchaseEntryDetailPanelTypes("710", "720", "730");

function normalizedValue(value) {
    return String(value ?? "").trim();
}

function panelType(panelInfo) {
    return normalizedValue(DicVal(panelInfo, "type", ""));
}

function hasRawPurchaseReference(entryData) {
    return normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.referenceSlipNo, "")) !== "";
}

function isEditingReferenceSlipNo(context) {
    return context.reason === "base-value-changed"
        && context.changedColumnId === HEADER_FIELD_IDS.referenceSlipNo
        && context.edit === true;
}

function isReturnSlip(entryData) {
    return normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.denSyubetu, "")) === "02";
}

function isNebikiOrNemasi(entryData) {
    const denSyubetu = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.denSyubetu, ""));
    return denSyubetu === "03" || denSyubetu === "04";
}

function isDomesticPurchase(entryData) {
    return normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.kaigaiKbn, "")) === "1";
}

function isReturnFromRawPurchase(entryData) {
    return hasRawPurchaseReference(entryData) && isReturnSlip(entryData);
}

function shouldClearReferenceDetail(context, entryData) {
    return context.edittingcode === "" && hasRawPurchaseReference(entryData) && isNebikiOrNemasi(entryData);
}

function isReturnControlledDetailPanel(panelInfo) {
    const currentPanelType = panelType(panelInfo);
    return currentPanelType === DETAIL_PANEL_TYPES.zai
        || currentPanelType === DETAIL_PANEL_TYPES.fzk
        || currentPanelType === DETAIL_PANEL_TYPES.sub;
}

function quantityColumnIds(panelInfo) {
    if (panelType(panelInfo) === DETAIL_PANEL_TYPES.zai) {
        return [DETAIL_COLUMN_IDS.siireTanSu, DETAIL_COLUMN_IDS.siireMeterSu];
    }
    return [DETAIL_COLUMN_IDS.siireSu];
}

function isInvoiceLocked(entryData) {
    if (isDomesticPurchase(entryData)) return true;
    const sourceCategoryCode = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.sanMotoDenKbn, ""));
    return sourceCategoryCode === SOURCE_CATEGORY_CODES.rawSalesIn || sourceCategoryCode === SOURCE_CATEGORY_CODES.rawIntTrade;
}

function runtimeState(context) {
    if (isEmpty(context.config.rawPurchaseRuntimeState)) {
        context.config.rawPurchaseRuntimeState = {
            snapshotReferenceNo: "",
            detailSnapshot: null,
            detailClearedForNebikiNemasi: false
        };
    }
    return context.config.rawPurchaseRuntimeState;
}

function captureReferenceDetailSnapshot(context, entryData) {
    const state = runtimeState(context);
    const referenceNo = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.referenceSlipNo, ""));

    if (referenceNo === "" || context.edittingcode !== "") {
        state.snapshotReferenceNo = "";
        state.detailSnapshot = null;
        state.detailClearedForNebikiNemasi = false;
        return;
    }

    if (state.snapshotReferenceNo !== referenceNo) {
        state.snapshotReferenceNo = referenceNo;
        state.detailSnapshot = null;
        state.detailClearedForNebikiNemasi = false;
    }

    if (!isEmpty(state.detailSnapshot)) return;

    const nextSnapshot = {};
    let hasRows = false;
    context.DetailGridList().forEach(panelInfo => {
        const rows = context.GridData(panelInfo);
        if (!Array.isArray(rows) || rows.length === 0) return;
        nextSnapshot[panelType(panelInfo)] = DeepCopy(rows);
        hasRows = true;
    });

    if (hasRows) {
        state.detailSnapshot = nextSnapshot;
    }
}

function restoreReferenceDetail(context) {
    const state = runtimeState(context);
    if (isEmpty(state.detailSnapshot)) return false;

    let changed = false;
    context.DetailGridList().forEach(panelInfo => {
        const restoreRows = DeepCopy(DicVal(state.detailSnapshot, panelType(panelInfo), []));
        const currentRows = context.GridData(panelInfo);
        if (JSON.stringify(currentRows ?? []) === JSON.stringify(restoreRows)) return;
        context.ApplyGridData(panelInfo, restoreRows);
        context.SyncTotalPanel(panelInfo, restoreRows, null);
        changed = true;
    });
    state.detailClearedForNebikiNemasi = false;
    return changed;
}

function clearReferenceDetail(context) {
    const state = runtimeState(context);
    let changed = false;
    context.DetailGridList().forEach(panelInfo => {
        const currentRows = context.GridData(panelInfo);
        if (Array.isArray(currentRows) && currentRows.length === 0) return;
        context.ApplyGridData(panelInfo, []);
        context.SyncTotalPanel(panelInfo, [], null);
        changed = true;
    });
    if (changed) {
        state.detailClearedForNebikiNemasi = true;
    }
    return changed;
}

function hasAnyDetailRows(context) {
    return context.DetailGridList().some(panelInfo => {
        const rows = context.GridData(panelInfo);
        return Array.isArray(rows) && rows.length !== 0;
    });
}

function applyBaseFieldStates(context, entryData) {
    if (isEditingReferenceSlipNo(context)) return false;

    const currentFields = Array.isArray(context.textfieldsall) ? context.textfieldsall : [];
    const sourceFields = Array.isArray(context.originalTextfieldsall) && context.originalTextfieldsall.length !== 0
        ? context.originalTextfieldsall
        : currentFields;
    if (!Array.isArray(sourceFields) || sourceFields.length === 0 || currentFields.length === 0) return false;

    const hasReference = hasRawPurchaseReference(entryData);
    const invoiceLocked = isInvoiceLocked(entryData);
    const nextFields = buildUpdatedRawPurchaseEntryFields(
        currentFields,
        sourceFields,
        [HEADER_FIELD_IDS.denKbn, HEADER_FIELD_IDS.denSyubetu, HEADER_FIELD_IDS.invoiceNo],
        (field, originalField) => {
            const originalLocked = DicVal(originalField, "locked", false) === true;
            let changed = false;
            const nextField = { ...field };

            if (field.columnid === HEADER_FIELD_IDS.denKbn) {
                const nextLocked = hasReference ? true : originalLocked;
                if ((DicVal(field, "locked", false) === true) !== nextLocked) {
                    nextField.locked = nextLocked;
                    changed = true;
                }
            }

            if (field.columnid === HEADER_FIELD_IDS.denSyubetu) {
                const baseOptions = Array.isArray(DicVal(originalField, "options", null))
                    ? originalField.options
                    : [];
                const nextOptions = hasReference
                    ? baseOptions.filter(option => ["02", "03", "04"].includes(normalizedValue(option.code)))
                    : baseOptions;
                if (!areRawPurchaseEntryFieldOptionsEqual(field.options, nextOptions)) {
                    nextField.options = nextOptions;
                    changed = true;
                }
            }

            if (field.columnid === HEADER_FIELD_IDS.invoiceNo) {
                const nextLocked = invoiceLocked ? true : originalLocked;
                if ((DicVal(field, "locked", false) === true) !== nextLocked) {
                    nextField.locked = nextLocked;
                    changed = true;
                }
            }

            return changed ? nextField : field;
        }
    );

    if (nextFields !== null) {
        context.ReplaceBaseFields(nextFields);
        return true;
    }
    return false;
}

function applyHeaderValueRules(context, entryData) {
    let changed = false;
    const nextEntryData = DeepCopy(entryData);

    const nextNebikiNemasi = isNebikiOrNemasi(nextEntryData) ? NumConv(0) : "";
    if (DicVal(nextEntryData, HEADER_FIELD_IDS.nebikiNemasi, "") !== nextNebikiNemasi) {
        nextEntryData[HEADER_FIELD_IDS.nebikiNemasi] = nextNebikiNemasi;
        changed = true;
    }

    if (changed) {
        context.entrydataRef.current = nextEntryData;
    }
    return changed;
}

function applyDetailRowRules(context, entryData, panelInfo) {
    const currentRows = context.GridData(panelInfo);
    if (!Array.isArray(currentRows) || currentRows.length === 0) return false;

    const nextRows = DeepCopy(currentRows);
    const currentPanelType = panelType(panelInfo);
    const isForeign = normalizedValue(DicVal(entryData, HEADER_FIELD_IDS.kaigaiKbn, "")) === "2";
    const rate = NumConv(DicVal(entryData, HEADER_FIELD_IDS.shanaiRate, 0));
    let changed = false;

    nextRows.forEach(row => {
        if (Object.prototype.hasOwnProperty.call(row, DETAIL_COLUMN_IDS.tankaF) && isDomesticPurchase(entryData) && NumConv(row[DETAIL_COLUMN_IDS.tankaF]) !== 0) {
            row[DETAIL_COLUMN_IDS.tankaF] = NumConv(0);
            changed = true;
        }

        if (isReturnFromRawPurchase(entryData)) {
            let tanka = NumConv(DicVal(row, DETAIL_COLUMN_IDS.tanka, 0));
            let tankaF = NumConv(DicVal(row, DETAIL_COLUMN_IDS.tankaF, 0));
            const qty = currentPanelType === DETAIL_PANEL_TYPES.zai
                ? NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireMeterSu, 0))
                : NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireSu, 0));

            // 画面設計書では参照読込後の返品時にレート再計算結果を明細へ反映するとされているため、
            // 参照元の単価を固定値のまま使わず、現在の社内レートに合わせて単価と金額を再計算します。
            if (isForeign && rate !== 0) {
                if (tankaF !== 0 || tanka === 0) {
                    tanka = GetRound(rate * tankaF, 1);
                } else {
                    tankaF = GetDesitRound(tanka / rate, 1, 2);
                }
            } else {
                tankaF = NumConv(0);
            }

            const kingaku = GetRound(qty * tanka, 0);
            const kingakuF = isForeign ? GetDesitRound(qty * tankaF, 0, 2) : NumConv(0);

            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.tanka, 0)) !== tanka) {
                row[DETAIL_COLUMN_IDS.tanka] = tanka;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.tankaF, 0)) !== tankaF) {
                row[DETAIL_COLUMN_IDS.tankaF] = tankaF;
                changed = true;
            }
            if (currentPanelType === DETAIL_PANEL_TYPES.zai) {
                if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTanka, 0)) !== tanka) {
                    row[DETAIL_COLUMN_IDS.meterTanka] = tanka;
                    changed = true;
                }
                if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.meterTankaF, 0)) !== tankaF) {
                    row[DETAIL_COLUMN_IDS.meterTankaF] = tankaF;
                    changed = true;
                }
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingaku, 0)) !== kingaku) {
                row[DETAIL_COLUMN_IDS.siireKingaku] = kingaku;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.siireKingakuF, 0)) !== kingakuF) {
                row[DETAIL_COLUMN_IDS.siireKingakuF] = kingakuF;
                changed = true;
            }
            if (NumConv(DicVal(row, DETAIL_COLUMN_IDS.kingakuZ, 0)) !== kingaku) {
                row[DETAIL_COLUMN_IDS.kingakuZ] = kingaku;
                changed = true;
            }
        }

        if (currentPanelType !== DETAIL_PANEL_TYPES.zai) return;

        if (isNebikiOrNemasi(entryData)) {
            if (NumConv(row[DETAIL_COLUMN_IDS.siireTanSu]) !== 0) {
                row[DETAIL_COLUMN_IDS.siireTanSu] = NumConv(0);
                changed = true;
            }
            if (NumConv(row[DETAIL_COLUMN_IDS.siireMeterSu]) !== 1) {
                row[DETAIL_COLUMN_IDS.siireMeterSu] = NumConv(1);
                changed = true;
            }
        }

        if (!isNebikiOrNemasi(entryData) && !isReturnFromRawPurchase(entryData) && normalizedValue(DicVal(row, DETAIL_COLUMN_IDS.ssdNo, "")) !== "") {
            row[DETAIL_COLUMN_IDS.ssdNo] = "";
            changed = true;
        }
    });

    if (!changed) return false;

    context.ApplyGridData(panelInfo, nextRows);
    context.SyncTotalPanel(panelInfo, nextRows, null);
    return true;
}

function applyDetailRules(context, entryData) {
    captureReferenceDetailSnapshot(context, entryData);
    const state = runtimeState(context);

    let changed = false;

    if (shouldClearReferenceDetail(context, entryData)) {
        changed = clearReferenceDetail(context) || changed;
    } else if (context.edittingcode === "" && isReturnFromRawPurchase(entryData) && (state.detailClearedForNebikiNemasi || !hasAnyDetailRows(context))) {
        changed = restoreReferenceDetail(context) || changed;
    }

    context.DetailGridList().forEach(panelInfo => {
        changed = applyDetailRowRules(context, entryData, panelInfo) || changed;
    });

    return changed;
}

function applyRawPurchaseEntryRules(context) {
    const entryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
    const reason = normalizedValue(context.reason);
    const changedColumnId = normalizedValue(context.changedColumnId);

    if (reason === "base-value-changed" && changedColumnId === HEADER_FIELD_IDS.referenceSlipNo && context.edit === true) {
        return;
    }

    if (reason === "grid-row-value-changed") {
        applyDetailRules(context, entryData);
        return;
    }

    applyBaseFieldStates(context, entryData);
    let headerValueChanged = false;
    let detailRuleChanged = false;

    if (reason === "base-value-changed") {
        if (changedColumnId === HEADER_FIELD_IDS.denSyubetu) {
            headerValueChanged = applyHeaderValueRules(context, entryData);
            detailRuleChanged = applyDetailRules(context, entryData);
        } else if (changedColumnId === HEADER_FIELD_IDS.kaigaiKbn
            || changedColumnId === HEADER_FIELD_IDS.shanaiRate
            || changedColumnId === HEADER_FIELD_IDS.sanMotoDenKbn) {
            detailRuleChanged = applyDetailRules(context, entryData);
        }
    } else {
        headerValueChanged = applyHeaderValueRules(context, entryData);
        detailRuleChanged = applyDetailRules(context, entryData);
    }

	    if (headerValueChanged || detailRuleChanged) {
	        context.TriggerEntryInputRender();
	    }
	}

function gridLockCell(context) {
    const entryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
    const colDef = DicVal(context.params, "colDef", {});
    const columnId = normalizedValue(DicVal(colDef, "colId", DicVal(colDef, "field", "")));

    if (isReturnFromRawPurchase(entryData)) {
        return !isReturnControlledDetailPanel(context.panelInfo)
            ? context.edit
            : context.edit && quantityColumnIds(context.panelInfo).includes(columnId);
    }

    if (columnId === DETAIL_COLUMN_IDS.tankaF && isDomesticPurchase(entryData)) {
        return false;
    }

    if (panelType(context.panelInfo) === DETAIL_PANEL_TYPES.zai &&
        isNebikiOrNemasi(entryData) &&
        [DETAIL_COLUMN_IDS.siireTanSu, DETAIL_COLUMN_IDS.siireMeterSu].includes(columnId)) {
        return false;
    }

    if (columnId === DETAIL_COLUMN_IDS.ssdNo) {
        return isNebikiOrNemasi(entryData);
    }

    return context.edit;
}

function queryAddRow(context) {
    const entryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
    if (!isReturnFromRawPurchase(entryData) || !isReturnControlledDetailPanel(context.panelInfo)) return true;

    if (context.rowcount === undefined || context.rowcount === null) {
        context.fnc.current.warnmsg("返品時は明細を追加できません。");
    }
    return false;
}

function queryInsertRow(context) {
    return queryAddRow(context);
}

const entryConfig = {
    screenId: "RawPurchaseEntryRawPurchase",
    apiController: "RawPurchase",
    dataController: "RawPurchase",
    layoutController: "RawPurchase",
    formKey: "RawPurchaseEntryRawPurchase",
    listController: "RawPurchase",
    callerIdentifier: "RawPurchase",
    sourceCategoryCode: "7",
    primarykeys: ["ZSR_DENNO"],
    sourceCategoryColumnId: RAW_PURCHASE_ENTRY_FIELD_IDS.sourceCategoryCode,
    materialCategoryColumnId: "ZSR_ZAIRYOKBN",
    materialCategoryStateKeys: [
        "materialCategoryCode"
    ],
    detailPanelTypesByMaterialCategory: detailPanelTypesByMaterialCategory,
    hiddenPanelTypesByMaterialCategory: buildRawPurchaseEntryHiddenPanelTypes("710", "720", "730"),
    totalPanelDefinitions: buildRawPurchaseEntryTotalPanelDefinitions("711", "721", "731"),
    entryHooks: {
        applyScreenRules: applyRawPurchaseEntryRules,
        gridLockCell: gridLockCell,
        queryAddRow: queryAddRow,
        queryInsertRow: queryInsertRow
    }
};

export const RawPurchaseEntryRawPurchase = React.forwardRef((props, ref) => {
    return <RawPurchaseEntryBase {...props} ref={ref} entryConfig={entryConfig} />;
});

